"use client"

import { IconButton, Text, Button } from "@/elements"
import { XMarkIcon } from "@heroicons/react/24/outline"
import React, { FC } from "react"
import Link from "next/link"
import { motion, AnimatePresence } from "motion/react"
import { ProBadge } from "./ProBadge"
import clsx from "clsx"
import { usePathname } from "next/navigation"

interface Props {
  onClose: () => void
}

const listItems = [
  {
    title: "Today",
    text: "Get full access to job board details and apply to unlimited jobs.",
  },
  {
    title: "In 24 hours",
    text: "You’ll get a reminder that your access is ending. You can re-purchase.",
  },
  {
    title: "After 24 hours",
    text: "You can access job applications and message prospective employers even after your day pass expires.",
  },
]

const fadeVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.5 } },
}

export const DayPassSuccessStep: FC<Props> = ({ onClose, ...props }) => {
  const pathname = usePathname()
  return (
    <div className="relative h-[601px] w-full">
      <div className="absolute right-4 top-4 z-30">
        <IconButton Icon={XMarkIcon} onClick={onClose} floating />
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key="step-1"
          className="absolute inset-0"
          variants={fadeVariants}
          initial="initial"
          animate="animate"
          exit="exit">
          <div className="absolute left-0 right-0 top-0 flex h-[300px] flex-col items-center justify-start py-[80px]">
            <div
              className="absolute bottom-0 left-0 h-[180px] w-full bg-gradient-to-t from-bg-1 via-bg-1 to-transparent"
              style={{
                zIndex: 1,
              }}
            />
            <div className="absolute left-0 right-0 top-0 z-0 h-full w-full">
              <video
                src="/subscriptions/success-video.mp4"
                autoPlay
                muted
                playsInline
                className="h-full w-full object-cover"
              />
            </div>
            <div className="relative z-10 flex w-full flex-row items-center justify-center">
              <ProBadge />
            </div>
            <div className="relative z-10 mt-4 max-w-[170px]">
              <Text variant="h6" className="text-center text-fg-1">
                Welcome to Nova Pro day pass
              </Text>
            </div>
          </div>
          <div className={clsx("relative z-10 flex w-full flex-row gap-4 px-8 pt-[205px]")}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="h-[342px] w-[36px] flex-shrink-0">
              <img
                src="https://d201dxmsht1mhv.cloudfront.net/Day-Pass-Success-Icon-Stack.png"
                alt="Day pass success icon stack"
                className="h-[342px] w-[36px] flex-shrink-0"
              />
            </motion.div>
            <div className="flex w-full flex-col gap-6">
              {listItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex flex-row items-center justify-between opacity-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}>
                  <div className="flex flex-col">
                    <Text variant="body2" className="text-fg-2">
                      {item.title}
                    </Text>
                    <Text>{item.text}</Text>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <motion.div
            className="absolute bottom-0 left-0 right-0 z-20 px-10 pb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}>
            <Link
              href="/jobs"
              onClick={() => {
                if (pathname && ["/jobs", "/"].includes(pathname)) {
                  onClose()
                }
              }}>
              <Button className="w-full">Get started</Button>
            </Link>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
